import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { getLang, t } from "@/lib/i18n";
import { Trophy, XCircle, CheckCircle2, ArrowRight } from "lucide-react";
import QuestionImage from "@/components/QuestionImage";

export default function ExamResults() {
  const [res, setRes] = useState(null);
  const lang = getLang();
  const nav = useNavigate();

  useEffect(() => {
    const r = sessionStorage.getItem("tvde_result");
    if (!r) { nav("/categories"); return; }
    setRes(JSON.parse(r));
  }, [nav]);

  if (!res) return <Layout><div className="max-w-3xl mx-auto px-6 py-16">Loading...</div></Layout>;

  return (
    <Layout>
      <div className="max-w-3xl mx-auto px-6 py-16 fade-in">
        <div className={`inline-flex items-center gap-3 px-4 py-2 rounded-full mb-6 ${res.passed ? "bg-emerald-100 text-emerald-800" : "bg-red-100 text-red-800"}`} data-testid="result-status">
          {res.passed ? <Trophy className="w-4 h-4" /> : <XCircle className="w-4 h-4" />}
          <span className="font-semibold text-sm uppercase tracking-wider">{res.passed ? t(lang, "passed") : t(lang, "failed")}</span>
        </div>
        <h1 className="font-serif text-6xl sm:text-7xl font-medium tracking-tight" data-testid="result-score">{res.score_pct}%</h1>
        <p className="text-muted-foreground mt-3">
          {res.correct_count} / {res.total} — {t(lang, "passed").toLowerCase()} @ {res.pass_mark}%
        </p>

        <div className="mt-12 border-t border-border pt-8">
          <h2 className="font-serif text-2xl font-medium mb-6">{t(lang, "review")}</h2>
          <div className="space-y-4" data-testid="review-list">
            {res.review.map((item, i) => (
              <div key={item.question_id} className={`p-5 rounded-md border-l-4 ${item.is_correct ? "border-emerald-600 bg-emerald-50/40 dark:bg-emerald-950/10" : "border-red-600 bg-red-50/40 dark:bg-red-950/10"}`} data-testid={`review-item-${i}`}>
                <div className="font-mono text-xs text-muted-foreground mb-2">#{i + 1}</div>
                <div className="font-medium mb-3 leading-snug">{item.text_pt}</div>
                <QuestionImage qid={item.question_id} imageFile={item.image_file} className="!mb-3" />
                <div className="space-y-1 text-sm">
                  {item.options_pt.map((o) => (
                    <div key={o.letter} className={`px-3 py-1.5 rounded ${
                      o.letter === item.correct_letter ? "bg-emerald-100 dark:bg-emerald-950/40 font-medium"
                      : o.letter === item.user_letter ? "bg-red-100 dark:bg-red-950/40 line-through"
                      : ""
                    }`}>
                      <span className="font-mono text-xs mr-2">{o.letter}</span> {o.text}
                      {o.letter === item.correct_letter && <CheckCircle2 className="inline w-3.5 h-3.5 ml-2 text-emerald-600" />}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-10 flex gap-3">
          <Button onClick={() => nav("/dashboard")} className="bg-foreground text-background rounded-full" data-testid="results-dashboard">
            {t(lang, "dashboard")}
          </Button>
          <Button onClick={() => nav("/categories")} variant="outline" className="rounded-full" data-testid="results-categories">
            {t(lang, "categories")} <ArrowRight className="w-4 h-4 ml-1" />
          </Button>
        </div>
      </div>
    </Layout>
  );
}
